import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch, apiImageUrl } from '../utils/streamApi';
import ConfirmDialog from '../components/ConfirmDialog';
import FacePhotoLightbox from '../components/FacePhotoLightbox';
import '../css/parvekshak.css';
import '../css/knownfaces.css';

function avatarColor(name) {
  const palette = ['#e74c3c','#c0392b','#922b21','#e84393','#c0392b','#b03a2e'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return palette[Math.abs(h) % palette.length];
}

function Toast({ msg, type }) {
  if (!msg) return null;
  return <div className={`pk-toast ${type}`}>{msg}</div>;
}

function BannedPhotoThumb({ name, file, onDelete, onClick }) {
  const [src,        setSrc]        = useState(null);
  const [errored,    setErrored]    = useState(false);
  const [delBusy,    setDelBusy]    = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  useEffect(() => {
    setErrored(false);
    apiImageUrl(`/ui/faces/banned/${encodeURIComponent(name)}/photo/${encodeURIComponent(file)}`)
      .then(url => setSrc(url));
  }, [name, file]);

  const doDelete = async () => {
    setConfirmDel(false);
    setDelBusy(true);
    try {
      await apiFetch(`/ui/faces/banned/${encodeURIComponent(name)}/photo/${encodeURIComponent(file)}`, { method: 'DELETE' });
      onDelete(file);
    } catch {
      setDelBusy(false);
    }
  };

  return (
    <>
    <div className="kf-thumb" onClick={() => src && !errored && onClick?.(src)}>
      {src && !errored
        ? <img src={src} alt={file} className="kf-thumb-img" style={{ cursor: 'zoom-in' }}
            onError={() => setErrored(true)} />
        : <div className="kf-thumb-placeholder">{src ? '⚠️' : '⏳'}</div>
      }
      <button className="kf-thumb-del" onClick={e => { e.stopPropagation(); setConfirmDel(true); }} disabled={delBusy} title="Delete capture">
        {delBusy ? '…' : '✕'}
      </button>
    </div>
    {confirmDel && (
      <ConfirmDialog title="Delete this capture?" confirmLabel="Delete"
        onConfirm={doDelete} onCancel={() => setConfirmDel(false)} />
    )}
    </>
  );
}

function BannedPersonCard({ person, onPersonEmpty, onUnbanned, onRenamed, expanded, onToggle }) {
  const [photos,       setPhotos]       = useState(null);
  const [lightbox,     setLightbox]     = useState(null);
  const [unbanBusy,    setUnbanBusy]    = useState(false);
  const [renaming,     setRenaming]     = useState(false);
  const [newName,      setNewName]      = useState('');
  const [renameBusy,   setRenameBusy]   = useState(false);
  const [confirmUnban, setConfirmUnban] = useState(false);
  const loadedRef      = useRef(false);
  const renameInputRef = useRef(null);

  const loadPhotos = useCallback(async () => {
    try {
      const r = await apiFetch(`/ui/faces/banned/${encodeURIComponent(person.name)}/photos`);
      const d = await r.json();
      setPhotos(d.photos || []);
    } catch {
      setPhotos([]);
    }
  }, [person.name]);

  useEffect(() => {
    if (expanded && !loadedRef.current) {
      loadedRef.current = true;
      loadPhotos();
    }
  }, [expanded, loadPhotos]);

  const toggle = () => onToggle();

  const handleDelete = (file) => {
    const next = photos.filter(p => p.file !== file);
    setPhotos(next);
    if (next.length === 0) onPersonEmpty(person.name);
  };

  const doUnban = async () => {
    setConfirmUnban(false);
    setUnbanBusy(true);
    try {
      await apiFetch(`/faces/banned/${encodeURIComponent(person.name)}`, { method: 'DELETE' });
      onUnbanned(person.name);
    } catch {
      setUnbanBusy(false);
    }
  };

  const startRename = (e) => {
    e.stopPropagation();
    setNewName(person.name);
    setRenaming(true);
    setTimeout(() => renameInputRef.current?.select(), 50);
  };

  const cancelRename = (e) => {
    e?.stopPropagation();
    setRenaming(false);
    setNewName('');
  };

  const submitRename = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const n = newName.trim();
    if (!n || n === person.name) { cancelRename(); return; }
    setRenameBusy(true);
    try {
      await apiFetch(`/faces/banned/${encodeURIComponent(person.name)}/rename?new_name=${encodeURIComponent(n)}`, { method: 'PUT' });
      setRenaming(false);
      onRenamed(person.name, n);
    } catch (err) {
      const msg = await err?.response?.json?.().catch?.(() => null);
      setRenameBusy(false);
    }
  };

  const color = avatarColor(person.name);

  return (
    <div className="kf-card" style={{ borderColor: 'rgba(231,76,60,0.3)' }}>
      <div className="kf-card-top" onClick={toggle} style={{ cursor: 'pointer' }}>
        <div className="kf-avatar" style={{ background: color, boxShadow: '0 0 0 3px rgba(231,76,60,0.35)' }}>
          {person.name.charAt(0).toUpperCase()}
        </div>
        <div className="kf-card-info">
          {renaming ? (
            <form onSubmit={submitRename} onClick={e => e.stopPropagation()}
              style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                ref={renameInputRef}
                className="alert-inline-input"
                style={{ padding: '5px 10px', fontSize: 14, width: 160, margin: 0 }}
                value={newName}
                onChange={e => setNewName(e.target.value)}
                disabled={renameBusy}
                onKeyDown={e => e.key === 'Escape' && cancelRename()}
              />
              <button type="submit" className="pk-btn danger" disabled={renameBusy}
                style={{ padding: '5px 12px', fontSize: 12, height: 30 }}>
                {renameBusy ? '…' : '✓ Save'}
              </button>
              <button type="button" className="pk-btn" disabled={renameBusy}
                onClick={cancelRename} style={{ padding: '5px 10px', fontSize: 12, height: 30 }}>
                Cancel
              </button>
            </form>
          ) : (
            <>
              <p className="kf-card-name">{person.name}</p>
              <div className="kf-card-meta">
                <span className="badge badge-banned">BANNED</span>
                <span className="badge badge-cam">
                  {photos !== null ? photos.length : person.file_count} capture{(photos?.length ?? person.file_count) !== 1 ? 's' : ''}
                </span>
              </div>
            </>
          )}
        </div>
        {!renaming && (
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
            <button className="kf-icon-btn" title="Rename" onClick={startRename}
              disabled={unbanBusy} style={{ fontSize: 13 }}>
              ✏️
            </button>
            <button className="kf-icon-btn unban" data-tip="Unban person"
              onClick={e => { e.stopPropagation(); setConfirmUnban(true); }} disabled={unbanBusy}>
              {unbanBusy ? '…' : '✓'}
            </button>
          </div>
        )}
        <span className="kf-chevron" style={{ marginLeft: 8 }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div className="kf-expanded">
          {photos === null ? (
            <div className="kf-photos-loading">⏳ Loading…</div>
          ) : photos.length === 0 ? (
            <div className="kf-photos-empty">No captures stored</div>
          ) : (
            <div className="kf-photos-strip">
              {photos.map((p, i) => (
                <BannedPhotoThumb
                  key={p.file}
                  name={person.name}
                  file={p.file}
                  onDelete={handleDelete}
                  onClick={src => setLightbox({ idx: i, src })}
                />
              ))}
            </div>
          )}
        </div>
      )}
      {lightbox && photos && (
        <FacePhotoLightbox
          photos={photos}
          idx={lightbox.idx}
          initialSrc={lightbox.src}
          personName={person.name}
          kind="banned"
          onClose={() => setLightbox(null)}
        />
      )}
      {confirmUnban && (
        <ConfirmDialog title={`Unban ${person.name}?`} message="They will be removed from the banned list."
          confirmLabel="Unban" confirmClass="danger"
          onConfirm={doUnban} onCancel={() => setConfirmUnban(false)} />
      )}
    </div>
  );
}

export default function BannedFacesPage() {
  const PER_PAGE = 20;

  const [persons,     setPersons]     = useState([]);
  const [total,       setTotal]       = useState(0);
  const [hasMore,     setHasMore]     = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [fetching,    setFetching]    = useState(false);
  const [toast,       setToast]       = useState(null);
  const [expandedSet, setExpandedSet] = useState(new Set());

  const sentinelRef  = useRef(null);
  const fetchingRef  = useRef(false);
  const pageRef      = useRef(0);
  const hasMoreRef   = useRef(false);

  const showToast = (msg, type = 'ok') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  };

  const handleUnbanned = (name) => {
    setPersons(prev => prev.filter(p => p.name !== name));
    setTotal(prev => prev - 1);
    showToast(`${name} unbanned`);
  };

  const loadPage = useCallback(async (p, reset = false) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (reset) setLoading(true);
    else       setFetching(true);
    try {
      const r = await apiFetch(`/ui/faces/banned?page=${p}&per_page=${PER_PAGE}`);
      const d = await r.json();
      const items = d.persons || [];
      setPersons(prev => reset ? items : [...prev, ...items]);
      setTotal(d.total || 0);
      pageRef.current = p;
      const more = items.length === PER_PAGE;
      hasMoreRef.current = more;
      setHasMore(more);
    } catch {
      showToast('Failed to load banned faces', 'err');
    } finally {
      fetchingRef.current = false;
      setLoading(false);
      setFetching(false);
    }
  }, []);

  useEffect(() => { loadPage(0, true); }, [loadPage]);

  // Infinite scroll observer
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || fetching || loading) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMoreRef.current && !fetchingRef.current) {
        loadPage(pageRef.current + 1, false);
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, fetching, loading, loadPage]);

  const refresh = () => {
    pageRef.current    = 0;
    hasMoreRef.current = false;
    loadPage(0, true);
  };

  const expandAll   = () => setExpandedSet(new Set(persons.map(p => p.name)));
  const collapseAll = () => setExpandedSet(new Set());
  const toggleOne   = (name) => setExpandedSet(prev => {
    const next = new Set(prev);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });

  return (
    <div className="pk-page">
      <div className="page-toolbar">
        <span className="tb-label">{total} banned</span>
        <div className="tb-sep" />
        <button className="tb-ghost-btn" onClick={expandAll}>Expand All</button>
        <button className="tb-ghost-btn" onClick={collapseAll}>Collapse All</button>
        <div className="tb-sep" />
        <button className="tb-ghost-btn" onClick={refresh}>↺ Refresh</button>
      </div>

      {/* Note */}
      {persons.length > 0 && (
        <div style={{ marginBottom:20, padding:'10px 16px', background:'rgba(231,76,60,0.08)',
          border:'1px solid rgba(231,76,60,0.2)', borderRadius:10, fontSize:13, color:'#e74c3c' }}>
          ⚠️ Banned persons trigger immediate alerts on all cameras. Remove only if the threat is resolved.
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="pk-loading">⏳ Loading…</div>
      ) : persons.length === 0 ? (
        <div className="pk-empty">
          <div className="pk-empty-icon">✅</div>
          <h3>No banned persons</h3>
          <p>Ban people from an alert — open Alerts and use the 🚫 Ban button.</p>
        </div>
      ) : (
        <div className="kf-list">
          {persons.map(p => (
            <BannedPersonCard
              key={p.name}
              person={p}
              expanded={expandedSet.has(p.name)}
              onToggle={() => toggleOne(p.name)}
              onPersonEmpty={name => {
                setPersons(prev => prev.filter(x => x.name !== name));
                setTotal(prev => prev - 1);
              }}
              onUnbanned={handleUnbanned}
              onRenamed={(oldName, newName) => {
                setPersons(prev => prev.map(x => x.name === oldName ? { ...x, name: newName } : x));
                showToast(`Renamed to ${newName}`);
              }}
            />
          ))}
        </div>
      )}

      {!loading && <div ref={sentinelRef} style={{ height: 1 }} />}
      {fetching && <div className="pk-loading" style={{ padding: '10px 0' }}>Loading more…</div>}

      <Toast msg={toast?.msg} type={toast?.type} />
    </div>
  );
}
